import { API_ENDPOINT } from "./api";
import { authenticatedFetch } from "./auth";

/**
 * Handles the creation of a file and file-url of a correct pronunciation of a given sentence in a given language
 * @param {string} sentence - User-inputted (or auto generated) sentence they want to hear the correct pronunciation of
 * @param {string} language - User-selected language (de-DE, etc.)
 *
 * @returns {Promise} - API response containing a dict with the field 'media_url', public URL to access the audio file
 */
export const synthesizeSpeech = async (sentence_data, selected_language) => {
    try {
        const response = await fetch(`${API_ENDPOINT}/pronunciation/synth/`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                sentence_data,
                selected_language,
            }),
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.detail || "Failed to fetch sentence pronunciation");
        }

        return await response.json();
    } catch (error) {
        // Re-throw error for handling by caller
        throw error;
    }
};

/**
 * Handles the generation of a random sentence in the user-selected language
 * @param {string} language - the user-selected langauge
 *
 * @returns {string} - random sentence in the user-selected language
 */
export const getRandomSentence = async (language) => {
    const response = await fetch(`${API_ENDPOINT}/sentence/random/${language}/`);

    return await response.json();
};

// Convert Blob to Base64 Data URL (returns a Promise)
function blobToDataURL(blob) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = function (e) {
            resolve(e.target.result);
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });
}

/**
 * Request pronunciation audio for a given sentence in the specified language.
 * @param {blob} audio_data - The user recording
 * @param {string} sentence_data - The sentence to synthesize.
 * @param {string} selected_language - Language code (e.g. "de-DE", "en-GB", "fr-FR").
 * @returns {Promise<Object>} a celery task id that can later be polled to review status.
 */
export const analyzeSpeech = async (
    audio_data,
    audio_mimetype,
    sentence_data,
    selected_language,
    origin_url = null,
) => {
    const audio_data_url = await blobToDataURL(audio_data);
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    try {
        const response = await authenticatedFetch(`${API_ENDPOINT}/pronunciation/analyze/`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                audio_data_url,
                audio_mimetype,
                sentence_data,
                selected_language,
                timezone,
                ...(origin_url ? { origin_url } : {}),
            }),
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.detail || "Failed to analyze pronunciation");
        }

        return await response.json();
    } catch (error) {
        // Re-throw error for handling by caller
        throw error;
    }
};

/**
 * Check poll status for a given celery id.
 * @param {string} task_id - celery task id, previously given out by the server
 * @returns {Dict} {'status': task.status, 'result': task.result if task.successful() else None}
 */
export const pollStatus = async (task_id) => {
    try {
        const response = await fetch(
            `${API_ENDPOINT}/pronunciation/pollStatus/?task_id=${encodeURIComponent(task_id)}`,
            {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                },
            },
        );

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.detail || "Failed to poll celery");
        }

        return await response.json();
    } catch (error) {
        // Re-throw error for handling by caller
        throw error;
    }
};

/**
 * Get pronunciation results for a shared result.
 * @param {string} share_code - share_code, currently 6 chars long
 * @returns {Dict} {'sentence': str, 'recognized_sentence': str, scores: {accuracy: 87, ...}}
 */
export const getSharedResults = async (share_code) => {
    try {
        const response = await fetch(`${API_ENDPOINT}/pronunciation/shared_data/${share_code}/`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
            },
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.detail || "Failed to fetch shared results");
        }

        return await response.json();
    } catch (error) {
        // Re-throw error for handling by caller
        throw error;
    }
};
